/**
 * リワード広告のモック・オーバーレイ。
 *
 * 役割:
 *   - `services/ads.ts` の `registerRewardedOverlay()` にコールバックを登録し、
 *     表示要求が来たらオーバーレイを mount する。
 *   - スキップ可能タイミング (`AD_MOCK_SKIP_AFTER_MS`) までは閉じるボタンを disabled。
 *   - カウントダウンが完了したら `rewarded=true` で onClose、スキップなら
 *     `rewarded=false` で onClose を呼ぶ。
 *   - BGM/SE のミュートは `services/ads.ts:showRewardedAd` 側で setAdMuted +
 *     suspendForAd を呼んでいるため、本コンポーネントは UI のみに専念する。
 *
 * 設計判断:
 *   - 「広告再生 = 没入する全画面 UI」という審査ガイドの体裁を崩さないため、
 *     オーバーレイは画面全体を覆う z-index 9999 の固定ポジション。
 *   - スキップ前に閉じるボタンを押した場合は `rewarded=false`（報酬なし）として
 *     扱う。AdMob 本番経路の `Dismissed` イベントと挙動を揃えるため。
 *   - キャンセル時にも必ず onClose が呼ばれること（多重 resolve は ads.ts 側で
 *     ガードしているが、UI 側でも `closingRef` で一度きりに制限）。
 *
 * 注意:
 *   - 実機 AdMob 統合時は本コンポーネントは表示されない（`getAdMode()='production'`
 *     かつ ID 設定済なら ads.ts は AdMob 経路に直接行くため）。
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type { MutableRefObject } from 'react'
import {
  AD_MOCK_REWARDED_DURATION_MS,
  AD_MOCK_SKIP_AFTER_MS,
  getAdMode,
  type AdSlotType,
} from '../config/ads'
import {
  initializeYahooAdTag,
  registerRewardedOverlay,
  unregisterRewardedOverlay,
} from '../services/ads'
import { t, tx } from '../i18n/translations'

interface OverlayState {
  slot: AdSlotType
  onClose: (rewarded: boolean) => void
  yahooTagId?: string
}

const TICK_MS = 100
const YAHOO_REWARDED_CLOSE_AFTER_MS = 30000

export function AdRewardedOverlay() {
  const [state, setState] = useState<OverlayState | null>(null)
  // 経過時刻（ms）。100ms ごとに進める。再レンダで毎回 setState するため抑制目的で ref も保持。
  const [elapsedMs, setElapsedMs] = useState(0)
  const [yahooLoadFailed, setYahooLoadFailed] = useState(false)
  // setInterval の id。unmount / 完了時に確実に clear する。
  const tickerRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const yahooLoadTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const yahooObserverRef = useRef<MutationObserver | null>(null)
  // onClose の多重呼び出しを防ぐ（カウントダウン完了 + スキップが同時発火するレース対策）。
  const closingRef = useRef(false)
  // onClose を ref 化して finishAndClose の依存配列を空にする。state 依存だと
  // state 更新のたびに finishAndClose の参照が変わり、カウントダウン完了 useEffect が
  // 再実行されてレースウィンドウが広がる。
  const onCloseRef = useRef<((rewarded: boolean) => void) | null>(null)

  const finishAndClose = useCallback((rewarded: boolean) => {
    if (closingRef.current) return
    closingRef.current = true
    const cb = onCloseRef.current
    onCloseRef.current = null
    // setState は非同期だが、onClose 呼び出しは即時行う（次のレンダで unmount される）。
    setState(null)
    setElapsedMs(0)
    if (cb) cb(rewarded)
  }, [])

  useEffect(() => {
    registerRewardedOverlay((slot, onClose, yahooTagId) => {
      // 新たな表示要求。前回の closingRef を解除し、state をセット。
      closingRef.current = false
      onCloseRef.current = onClose
      setElapsedMs(0)
      setYahooLoadFailed(false)
      setState({ slot, onClose, yahooTagId })
    })
    return () => {
      unregisterRewardedOverlay()
      if (tickerRef.current) clearInterval(tickerRef.current)
      if (yahooLoadTimerRef.current) clearTimeout(yahooLoadTimerRef.current)
      yahooObserverRef.current?.disconnect()
    }
  }, [])

  // state がセットされている間だけタイマーを動かす。
  useEffect(() => {
    if (!state) {
      if (tickerRef.current) {
        clearInterval(tickerRef.current)
        tickerRef.current = null
      }
      return
    }
    tickerRef.current = setInterval(() => {
      setElapsedMs((prev) => prev + TICK_MS)
    }, TICK_MS)
    return () => {
      if (tickerRef.current) {
        clearInterval(tickerRef.current)
        tickerRef.current = null
      }
    }
  }, [state])

  // カウントダウン完了 → 自動的に rewarded=true で閉じる。
  useEffect(() => {
    if (!state) return
    if (state.yahooTagId) return
    if (elapsedMs < AD_MOCK_REWARDED_DURATION_MS) return
    finishAndClose(true)
  }, [elapsedMs, state, finishAndClose])

  const remainingMs = useMemo(() => {
    return Math.max(0, AD_MOCK_REWARDED_DURATION_MS - elapsedMs)
  }, [elapsedMs])

  const canSkip = elapsedMs >= AD_MOCK_SKIP_AFTER_MS

  if (!state) return null

  if (state.yahooTagId) {
    return (
      <YahooRewardedOverlay
        state={state}
        elapsedMs={elapsedMs}
        canClose={elapsedMs >= YAHOO_REWARDED_CLOSE_AFTER_MS}
        loadFailed={yahooLoadFailed}
        onLoadFailed={() => setYahooLoadFailed(true)}
        onClose={finishAndClose}
        loadTimerRef={yahooLoadTimerRef}
        observerRef={yahooObserverRef}
      />
    )
  }

  return (
    <div
      className="ad-mock-overlay"
      role="dialog"
      aria-modal="true"
      aria-label={t('adPlayingAria')}
    >
      <div className="ad-mock-overlay__badge">{t('adBadge')}</div>
      <div className="ad-mock-overlay__panel">
        <div className="ad-mock-overlay__spinner" aria-hidden="true" />
        <p className="ad-mock-overlay__title">
          {state.slot === 'stamina' ? t('recoverFullAd') : t('dailyBonusAd')}
        </p>
        <p className="ad-mock-overlay__countdown">
          {tx('adRemaining', { seconds: Math.ceil(remainingMs / 1000) })}
        </p>
        {/* 開発用のヒントは mock モード時のみ表示。本番(production)で AdMob が
            未統合の場合のフォールバックでも、エンドユーザーには表示しない。 */}
        {getAdMode() === 'mock' && (
          <p className="ad-mock-overlay__hint">
            {t('adDevHint')}
          </p>
        )}
      </div>
      <button
        type="button"
        className="ad-mock-overlay__skip"
        onClick={() => finishAndClose(false)}
        disabled={!canSkip}
        aria-label={t('adSkipAria')}
      >
        {canSkip
          ? t('adSkip')
          : tx('adSkipAfter', {
            seconds: Math.ceil((AD_MOCK_SKIP_AFTER_MS - elapsedMs) / 1000),
          })}
      </button>
    </div>
  )
}

export default AdRewardedOverlay

interface YahooRewardedOverlayProps {
  state: OverlayState
  elapsedMs: number
  canClose: boolean
  loadFailed: boolean
  onLoadFailed: () => void
  onClose: (rewarded: boolean) => void
  loadTimerRef: MutableRefObject<ReturnType<typeof setTimeout> | null>
  observerRef: MutableRefObject<MutationObserver | null>
}

const YAHOO_LOAD_TIMEOUT_MS = 10000

function YahooRewardedOverlay({
  state,
  elapsedMs,
  canClose,
  loadFailed,
  onLoadFailed,
  onClose,
  loadTimerRef,
  observerRef,
}: YahooRewardedOverlayProps) {
  const elementId = useMemo(() => `yads-reward-${state.yahooTagId}`, [state.yahooTagId])
  const initializedRef = useRef(false)

  useEffect(() => {
    if (!state.yahooTagId || initializedRef.current) return
    const timer = window.setTimeout(() => {
      const slot = document.getElementById(elementId)
      if (!slot) {
        onLoadFailed()
        return
      }
      initializedRef.current = initializeYahooAdTag(elementId, state.yahooTagId ?? '')
      observerRef.current = new MutationObserver((mutations) => {
        const hasAdContent = mutations.some((mutation) =>
          Array.from(mutation.addedNodes).some((node) => {
            if (node.nodeType !== Node.ELEMENT_NODE) return false
            const el = node as Element
            const tag = el.tagName?.toUpperCase()
            return tag === 'IFRAME' || tag === 'IMG' || tag === 'VIDEO' ||
              el.querySelector?.('iframe, img, video') != null
          }),
        )
        if (hasAdContent) {
          observerRef.current?.disconnect()
        }
      })
      observerRef.current.observe(slot, { childList: true, subtree: true })
      loadTimerRef.current = window.setTimeout(() => {
        if (!slot.querySelector('iframe, img, video')) onLoadFailed()
        observerRef.current?.disconnect()
      }, YAHOO_LOAD_TIMEOUT_MS)
    }, 100)
    return () => window.clearTimeout(timer)
  }, [elementId, loadTimerRef, observerRef, onLoadFailed, state.yahooTagId])

  const close = () => {
    const hasContent = document.getElementById(elementId)?.querySelector('iframe, img, video') != null
    onClose(!loadFailed && hasContent)
  }

  return (
    <div className="ad-mock-overlay" role="dialog" aria-modal="true" aria-label={t('adPlayingAria')}>
      <div className="ad-mock-overlay__badge">REWARD</div>
      <div className="ad-mock-overlay__panel ad-mock-overlay__panel--yahoo">
        <div id={elementId} className="ad-mock-overlay__yahoo-slot">
          {loadFailed ? (
            <p className="ad-mock-overlay__hint">広告を読み込めませんでした</p>
          ) : (
            <>
              <div className="ad-mock-overlay__spinner" aria-hidden="true" />
              <p className="ad-mock-overlay__title">
                {state.slot === 'stamina' ? t('recoverFullAd') : t('dailyBonusAd')}
              </p>
            </>
          )}
        </div>
      </div>
      <button
        type="button"
        className="ad-mock-overlay__skip"
        onClick={close}
        disabled={!canClose && !loadFailed}
        aria-label={t('adSkipAria')}
      >
        {canClose || loadFailed
          ? t('close')
          : tx('adSkipAfter', {
            seconds: Math.ceil((YAHOO_REWARDED_CLOSE_AFTER_MS - elapsedMs) / 1000),
          })}
      </button>
    </div>
  )
}
